"use client";

import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useTransition, useEffect, useRef } from "react";
import { locales } from "@/i18n/locales";
import Image from "next/image";
import { updateUserLocale } from "@/app/[locale]/actions/locale";
import { cn } from "@/lib/utils";

// Create a component for the flag icon
const FlagIcon = ({ countryCode, className = "" }: { countryCode: string; className?: string }) => {
  return (
    <Image
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`}
      alt={`${countryCode} flag`}
      width={24}
      height={18}
      className={`inline-block rounded-sm ${className}`}
      style={{ width: '24px', height: '18px' }}
    />
  );
};

interface HorizontalLanguageSelectorProps {
  className?: string;
  onLanguageChange?: (locale: string) => void;
}

const HorizontalLanguageSelector = ({
  className = "",
  onLanguageChange
}: HorizontalLanguageSelectorProps) => {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const selectedButtonRef = useRef<HTMLButtonElement>(null);

  // Scroll to selected item on mount and when locale changes
  useEffect(() => {
    if (selectedButtonRef.current && scrollAreaRef.current) {
      // Use a small delay to ensure layout is complete and images are loaded
      const scrollToSelected = () => {
        const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (!scrollContainer || !selectedButtonRef.current) return;

        // Get the container's scroll properties
        const containerRect = scrollContainer.getBoundingClientRect();

        // Calculate relative positions within the scroll container        
        const buttonLeftRelative = selectedButtonRef.current.offsetLeft;
        const buttonWidth = selectedButtonRef.current.offsetWidth;
        const containerWidth = containerRect.width;

        // Calculate the ideal scroll position to center the button
        const idealScrollLeft = buttonLeftRelative - (containerWidth / 2) + (buttonWidth / 2);

        // Apply bounds to prevent over-scrolling
        const maxScrollLeft = scrollContainer.scrollWidth - containerWidth;
        const finalScrollLeft = Math.max(0, Math.min(idealScrollLeft, maxScrollLeft));

        // Smooth scroll to position
        scrollContainer.scrollTo({
          left: finalScrollLeft,
          behavior: 'smooth'
        });
      };

      // Try immediately first
      scrollToSelected();

      // Also try after a short delay to handle any late layout changes
      const timeoutId = setTimeout(scrollToSelected, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [currentLocale]);

  // Add mousewheel handler for horizontal scrolling
  useEffect(() => {
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;

    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Prevent default vertical scrolling
      e.preventDefault();

      // Convert vertical scroll to horizontal
      // Use deltaY for vertical wheel movement, deltaX for trackpad horizontal scroll
      const scrollAmount = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      scrollContainer.scrollLeft += scrollAmount;
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleLocaleChange = (newLocale: string) => {
    if (newLocale === currentLocale) return;

    startTransition(async () => {
      try {
        // First update the user's preferred locale in the database
        await updateUserLocale(newLocale);

        // Then navigate using next-intl's navigation
        router.replace(pathname, { locale: newLocale });

        // Call the optional callback
        onLanguageChange?.(newLocale);
      } catch (error) {
        console.error("Error updating locale:", error);
        // Fallback to just navigation if server action fails
        router.replace(pathname, { locale: newLocale });
        onLanguageChange?.(newLocale);
      }
    });
  };

  return (
    <div className={cn("min-w-0", className)}>
      <ScrollArea className="rounded-md border whitespace-nowrap" ref={scrollAreaRef}>
        <div className="flex space-x-2 p-1 pb-3">
          {locales.map((locale) => {
            const isSelected = locale.id === currentLocale;
            return (
              <Button
                key={locale.id}
                ref={isSelected ? selectedButtonRef : undefined}
                variant={isSelected ? "default" : "outline"}
                size="sm"
                disabled={isPending}
                onClick={() => handleLocaleChange(locale.id)}
                className={cn(
                  "flex flex-col items-center gap-1 min-w-[80px] h-auto py-2 flex-shrink-0 border-0",
                  isSelected && "ring-2 ring-primary ring-offset-2"
                )}
              >
                <div className="flex items-center justify-center">
                  {locale.countryCode ? (
                    <FlagIcon countryCode={locale.countryCode} />
                  ) : (
                    <div className="w-6 h-[18px] bg-muted rounded-sm" />
                  )}
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-xs font-medium leading-tight">
                    {locale.nativeName}
                  </span>
                  <span className="text-[10px] text-muted-foreground leading-tight">
                    {locale.name}
                  </span>
                </div>
              </Button>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

export { HorizontalLanguageSelector };
